// @flow
import classNames from 'classnames';
import cleanSearchTerms from '../../utils/cleanSearchTerms';
import Form from 'react-bootstrap/lib/Form';
import { FormattedMessage } from 'react-intl';
import { getSellerUrl } from '../../utils/urls';
import isEqual from 'react-fast-compare';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import NoResults from '../../components/Pagination/NoResults';
import PageTitle from '../../components/PageTitle/PageTitle';
import { preventDefault } from '../../utils/preventDefault';
import React from 'react';
import SaveSearch from '../../components/SaveSearch/SaveSearch';
import SearchBreadcrumbPills from '../../components/SearchBreadcrumb/SearchBreadcrumbPills';
import searchDisplayTypes from '../../enums/searchDisplayTypes';
import SearchFacets from '../../components/SearchFacets/SearchFacets';
import SearchResultTabButtons from './SearchResultTabButtons';
import SearchSort from '../../components/SearchResults/SearchSort';
import ShouldLogInContainer from '../../components/ShouldLogIn/ShouldLogInContainer';
import styles from './SearchHeader.module.scss';
import ZeroResultsContainer from '../../components/ZeroResults/ZeroResultsContainer';

type Props = $ReadOnly<{|
    archivedSearch: boolean,
    buyNowItemsFound?: number,
    changeSearchStatus?: Function,
    clearAllParamsExceptCategories: Function,
    deleteSavedSearch: Function,
    distance: any,
    distanceSelected: Function,
    excludedFacets: any,
    facetOptionChecked: Function,
    facetOptionExcluded: Function,
    facets: any[],
    firstHouse: any,
    isLandingPage: boolean,
    liveItemsFound?: number,
    page: number,
    pageName: string,
    pageSize: number,
    pathname: string,
    publishDate: any,
    queryParams: any,
    rangeEntered: Function,
    ranges: any,
    removeCountryAndDistance: Function,
    removeFacetById: Function,
    removePublishedDate: Function,
    replaceCountry: Function,
    saveSearchCheckState: any,
    saveSearchState: any,
    searchSubmitted: boolean,
    searchTerm: string,
    showLandingPageLinks: boolean,
    showNoResults: boolean,
    soldItemsFound?: number,
    sort: string,
    sortOptions: any[],
    sortSelected: Function,
    submitSaveSearch: Function,
    taxonomyAllSelected: Function,
    taxonomyParentSelected: Function,
    taxonomySelected: Function,
    totalItemsFound: number,
    whiteLabel?: boolean,
    width: number,
|}>;

const SearchHeader = (props: Props) => {
    const {
        archivedSearch,
        buyNowItemsFound,
        changeSearchStatus,
        clearAllParamsExceptCategories,
        deleteSavedSearch,
        distance,
        distanceSelected,
        excludedFacets,
        facetOptionChecked,
        facetOptionExcluded,
        facets,
        firstHouse,
        isLandingPage,
        liveItemsFound,
        pageName,
        pathname,
        publishDate,
        queryParams,
        rangeEntered,
        ranges,
        removeCountryAndDistance,
        removeFacetById,
        removePublishedDate,
        replaceCountry,
        saveSearchCheckState,
        saveSearchState,
        searchSubmitted,
        searchTerm = '',
        showLandingPageLinks,
        showNoResults,
        soldItemsFound,
        sort,
        sortOptions,
        sortSelected,
        submitSaveSearch,
        taxonomyAllSelected,
        taxonomyParentSelected,
        taxonomySelected,
        totalItemsFound = 0,
        whiteLabel,
        width,
    } = props;

    const { isSavedSearch } = saveSearchCheckState;
    const displayBasedOnTotalItemsFound = totalItemsFound > 0;
    const tooltipMessage = <FormattedMessage id="follow_search_tooltip" values={{ token: null }} />;
    const resultsWrapperStyles = classNames(styles['results-wrapper'], {
        [styles.noResults]: !liveItemsFound,
    });

    const safeSearchTerm = cleanSearchTerms(searchTerm);

    return (
        <div className={styles['header-container']}>
            <div className={styles['header-row']}>
                {!whiteLabel && (
                    <SearchBreadcrumbPills
                        clearAllParamsExceptCategories={clearAllParamsExceptCategories}
                        distance={distance}
                        facetOptionChecked={facetOptionChecked}
                        facets={facets}
                        publishDate={publishDate}
                        ranges={ranges}
                        removeCountryAndDistance={removeCountryAndDistance}
                        removeFacetById={removeFacetById}
                        removePublishedDate={removePublishedDate}
                        taxonomyAllSelected={taxonomyAllSelected}
                        taxonomyParentSelected={taxonomyParentSelected}
                    />
                )}
            </div>
            <Form className={styles.controls} inline onSubmit={preventDefault}>
                {showNoResults && !archivedSearch && Boolean(safeSearchTerm) ? (
                    <ZeroResultsContainer className={resultsWrapperStyles} searchTerm={safeSearchTerm} />
                ) : showNoResults && !safeSearchTerm ? (
                    <div className={styles['no-results']}>
                        <NoResults>
                            <FormattedMessage id="search_no_results" />
                        </NoResults>
                    </div>
                ) : showNoResults && archivedSearch ? (
                    <div className={styles['no-results']}>
                        <NoResults>
                            <div>
                                <FormattedMessage id="search_no_results" />
                            </div>
                            {!isSavedSearch && (
                                <>
                                    <ShouldLogInContainer requireLogin runFunction={submitSaveSearch}>
                                        <span>
                                            <Link>
                                                <FormattedMessage id="save_search_create_search_alert_link" />
                                            </Link>
                                            &nbsp;
                                        </span>
                                    </ShouldLogInContainer>
                                    <FormattedMessage id="search_no_results_create_alert" />
                                </>
                            )}
                            {isSavedSearch && <FormattedMessage id="search_no_results_saved_alert" />}
                        </NoResults>
                    </div>
                ) : (
                    <div className={styles['results-wrapper']}>
                        <div className={styles['search-terms-row']}>
                            <div className={styles['search-terms-container']}>
                                {searchTerm && searchTerm !== '' && (
                                    <PageTitle
                                        className={classNames(styles.title, {
                                            [styles.hasSuggestion]: Boolean(firstHouse),
                                        })}
                                        title={
                                            <FormattedMessage
                                                id="searchResultsFor"
                                                values={{
                                                    searchTerm: safeSearchTerm,
                                                }}
                                            />
                                        }
                                    />
                                )}
                                {firstHouse && (
                                    <div className={styles.suggestion}>
                                        <FormattedMessage
                                            id="searchResults.lookingFor"
                                            values={{
                                                suggestion: (
                                                    <Link
                                                        key="house-link"
                                                        route
                                                        to={getSellerUrl(firstHouse.houseId, firstHouse.value)}
                                                    >
                                                        {firstHouse.value}
                                                    </Link>
                                                ),
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                            <div className={styles['search-saved-container']}>
                                <SaveSearch
                                    deleteSavedSearch={deleteSavedSearch}
                                    primary
                                    saveSearchCheckState={saveSearchCheckState}
                                    saveSearchState={saveSearchState}
                                    submitSaveSearch={submitSaveSearch}
                                    tooltipMessage={tooltipMessage}
                                />
                            </div>
                        </div>
                        <div className={styles['search-filter-row']}>
                            <SearchResultTabButtons
                                changeSearchStatus={changeSearchStatus}
                                displayMode={
                                    !archivedSearch
                                        ? searchDisplayTypes.DISPLAY_AUCTION
                                        : searchDisplayTypes.DISPLAY_PRICE_RESULTS
                                }
                                buyNowItemsFound={buyNowItemsFound}
                                liveItemsFound={liveItemsFound}
                                soldItemsFound={soldItemsFound}
                                isLoading={Boolean(searchSubmitted)}
                            />
                            {displayBasedOnTotalItemsFound && (
                                <div className={styles.filters}>
                                    <SearchFacets
                                        clearAllParamsExceptCategories={clearAllParamsExceptCategories}
                                        distance={distance}
                                        distanceSelected={distanceSelected}
                                        excludedFacets={excludedFacets}
                                        facetOptionChecked={facetOptionChecked}
                                        facetOptionExcluded={facetOptionExcluded}
                                        facets={facets}
                                        isLandingPage={isLandingPage}
                                        pageName={pageName}
                                        pathname={pathname}
                                        queryParams={queryParams}
                                        rangeEntered={rangeEntered}
                                        ranges={ranges}
                                        removeCountryAndDistance={removeCountryAndDistance}
                                        replaceCountry={replaceCountry}
                                        showLandingPageLinks={showLandingPageLinks}
                                        taxonomyAllSelected={taxonomyAllSelected}
                                        taxonomyParentSelected={taxonomyParentSelected}
                                        taxonomySelected={taxonomySelected}
                                        totalItemsFound={totalItemsFound}
                                        whiteLabel={whiteLabel}
                                        width={width}
                                    />
                                    <SearchSort
                                        archivedSearch={archivedSearch}
                                        sortSelected={sortSelected}
                                        sortOptions={sortOptions}
                                        sort={sort}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </Form>
        </div>
    );
};

export default React.memo<Props>(SearchHeader, isEqual);
